/**
 * Canonical form for the number-like identifiers the portal submits.
 *
 * The health-card input is masked as the patient types (`shared/lib/
 * inputFormats.ts` → "0015 520 174"), and the masked string is what lives in
 * component state — so the portal used to submit the SPACED number against a
 * stored unspaced one. The backend saw a difference, queued a physician-review
 * chip, and the physician had to clear a review item that carried no decision
 * (imp_11cf2120).
 *
 * The decision: numbers are canonical WITHOUT spaces or hyphens, on both
 * sides. This is the patient-portal half — it mirrors
 * `NUMBER_CANONICAL_KEYS` / `_canon_number` in
 * `med_form_app/backend/routers/patient.py`, so the same key set is
 * canonicalised at both ends and neither end has to guess.
 *
 * Round-trip safe: `formatValue` re-formats on read, so a digits-only stored
 * health-card number still RENDERS as "0015 520 174" in the portal.
 *
 * Deliberately NOT canonicalised: names, dates, `policy_number` (alphanumeric
 * policy strings whose separators are printed on the card, not an artefact of
 * our mask). Keep this list in step with the backend's.
 */

/** Keys whose values are canonicalised on submit. Mirrors the backend's
 *  NUMBER_CANONICAL_KEYS. */
export const NUMBER_CANONICAL_KEYS: readonly string[] = [
  "health_care_number",
  "insurance_id",
  "plan_number",
  "plan_id",
  "member_id",
  "group_id",
  "division_id",
  "certificate_number",
];

/** Strip whitespace and hyphens. Non-strings pass through untouched. */
export function canonNumber<T>(value: T): T | string {
  if (typeof value !== "string") return value;
  return value.replace(/[\s-]+/g, "");
}

/**
 * Return a copy of a snake_case profile dict with every number-like key in
 * canonical form, including the nested `insurance` sub-doc. Anything else
 * (names, dates, addresses, booleans, nulls) is copied through untouched.
 */
export function canonicalizeProfileNumbers(
  profile: Record<string, any> | null | undefined
): Record<string, any> {
  if (!profile || typeof profile !== "object") return profile as any;
  const out: Record<string, any> = { ...profile };
  for (const key of NUMBER_CANONICAL_KEYS) {
    if (typeof out[key] === "string") out[key] = canonNumber(out[key]);
  }
  const insurance = out.insurance;
  if (insurance && typeof insurance === "object" && !Array.isArray(insurance)) {
    const sub: Record<string, any> = { ...insurance };
    for (const key of NUMBER_CANONICAL_KEYS) {
      if (typeof sub[key] === "string") sub[key] = canonNumber(sub[key]);
    }
    out.insurance = sub;
  }
  return out;
}
